import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface SectionState {
  currentView: 'list' | 'create' | 'edit' | 'view';
  activeTabIndex: number;
  sectionId: string;
}

@Injectable({
  providedIn: 'root'
})
export class SectionStateService {
  private states: Map<string, BehaviorSubject<SectionState>> = new Map();
  private readonly STORAGE_PREFIX = 'doctor_state_';

  // ========== PERSISTENCIA ==========

  private getStorageKey(sectionId: string): string {
    const userId = localStorage.getItem('userId') || 'anonymous';
    return `${this.STORAGE_PREFIX}${sectionId}_${userId}`;
  }

  private saveToStorage(sectionId: string, state: SectionState): void {
    try {
      const key = this.getStorageKey(sectionId);
      localStorage.setItem(key, JSON.stringify({
        activeTabIndex: state.activeTabIndex,
        currentView: state.currentView
      }));
    } catch (e) {
      console.warn('Error saving state to localStorage:', e);
    }
  }

  private loadFromStorage(sectionId: string): Partial<SectionState> | null {
    try {
      const key = this.getStorageKey(sectionId);
      const stored = localStorage.getItem(key);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (e) {
      console.warn('Error loading state from localStorage:', e);
    }
    return null;
  }

  /**
   * Limpia el estado persistido para un usuario específico o el actual
   * Llamar desde logout para limpiar el estado
   */
  clearPersistedState(sectionId?: string): void {
    try {
      const userId = localStorage.getItem('userId') || 'anonymous';
      if (sectionId) {
        localStorage.removeItem(`${this.STORAGE_PREFIX}${sectionId}_${userId}`);
      } else {
        // Limpiar todos los estados del usuario
        const keysToRemove: string[] = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && key.startsWith(this.STORAGE_PREFIX) && key.endsWith(`_${userId}`)) {
            keysToRemove.push(key);
          }
        }
        keysToRemove.forEach(key => localStorage.removeItem(key));
      }
    } catch (e) {
      console.warn('Error clearing persisted state:', e);
    }
  }

  // ========== ESTADO ==========

  getState(sectionId: string): Observable<SectionState> {
    if (!this.states.has(sectionId)) {
      this.initializeSection(sectionId);
    }
    return this.states.get(sectionId)!.asObservable();
  }

  getCurrentState(sectionId: string): SectionState {
    if (!this.states.has(sectionId)) {
      this.initializeSection(sectionId);
    }
    return this.states.get(sectionId)!.value;
  }

  /**
   * Verifica si hay estado persistido para esta sección
   */
  hasPersistedState(sectionId: string): boolean {
    return this.loadFromStorage(sectionId) !== null;
  }

  /**
   * Obtiene el estado persistido sin inicializar la sección
   */
  getPersistedState(sectionId: string): Partial<SectionState> | null {
    return this.loadFromStorage(sectionId);
  }

  private initializeSection(sectionId: string): void {
    // Intentar cargar estado persistido
    const persisted = this.loadFromStorage(sectionId);
    
    const initialState: SectionState = {
      currentView: (persisted && persisted.currentView) ? persisted.currentView : 'list',
      activeTabIndex: (persisted && persisted.activeTabIndex !== undefined && persisted.activeTabIndex !== null) 
        ? persisted.activeTabIndex 
        : -1, // -1 indica que no hay tab definido
      sectionId: sectionId
    };
    this.states.set(sectionId, new BehaviorSubject(initialState));
  }

  setCurrentView(sectionId: string, view: 'list' | 'create' | 'edit' | 'view'): void {
    const currentState = this.getCurrentState(sectionId);
    this.updateState(sectionId, { ...currentState, currentView: view });
  }

  setActiveTab(sectionId: string, tabIndex: number): void {
    const currentState = this.getCurrentState(sectionId);
    this.updateState(sectionId, { ...currentState, activeTabIndex: tabIndex });
  }

  navigateToCreate(sectionId: string, tabIndex?: number): void {
    const currentState = this.getCurrentState(sectionId);
    const newTabIndex = tabIndex !== undefined ? tabIndex : currentState.activeTabIndex;
    this.updateState(sectionId, {
      currentView: 'create',
      activeTabIndex: newTabIndex,
      sectionId: sectionId
    });
  }

  navigateToList(sectionId: string): void {
    const currentState = this.getCurrentState(sectionId);
    this.updateState(sectionId, {
      ...currentState,
      currentView: 'list'
    });
  }

  navigateToView(sectionId: string): void {
    const currentState = this.getCurrentState(sectionId);
    this.updateState(sectionId, {
      ...currentState,
      currentView: 'view'
    });
  }

  navigateToEdit(sectionId: string): void {
    const currentState = this.getCurrentState(sectionId);
    this.updateState(sectionId, {
      ...currentState,
      currentView: 'edit'
    });
  }

  private updateState(sectionId: string, newState: SectionState): void {
    if (!this.states.has(sectionId)) {
      this.states.set(sectionId, new BehaviorSubject(newState));
    }
    this.states.get(sectionId)!.next(newState);
    // Persistir el estado
    this.saveToStorage(sectionId, newState);
  }

  reset(sectionId: string): void {
    this.clearPersistedState(sectionId);
    this.initializeSection(sectionId);
  }

  resetAll(): void {
    this.states.forEach((_, sectionId) => {
      this.clearPersistedState(sectionId);
    });
    this.states.clear();
  }
}
